/**
 * CortexGraphView — vibe-designer 1.174.0 Phase 5β2.
 *
 * Per-service viz on the CORTEX detail panel. Calls
 * GET /api/knowledge/services/cortex/graph?limit=50 → renders
 * react-force-graph-2d (canvas-based, lazy-loaded ~58 KB gzipped).
 *
 * LIVE-VERIFIED 2026-05-20: ALL 6 tenants have 0 episodes today, so
 * the production R3 will exercise the empty-state branch only. The
 * force-graph render path is covered by mock tests in
 * `__tests__/CortexGraphView.test.tsx` until any tenant gets actual
 * CORTEX data.
 */
import { useEffect, useState, Suspense, lazy } from 'react';
import { Loader2, RefreshCw, Share2 } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';

// WS-A1 (2026-07-12) — 2D / 3D CORTEX entity graph. Both canvases consume
// the SAME normalized {nodes, edges} shape; only the selected dimension's
// chunk downloads (react-force-graph-2d ~58 KB gzip OR react-force-graph-3d
// + three.js). Default is 3D per the explicit "3d graphiti relations" ask;
// 2D is retained as the safe fallback the empty-state path already exercises.
const ForceGraphCanvas2D = lazy(() => import('./_KnowledgeSigmaCanvas'));
const ForceGraphCanvas3D = lazy(() => import('./_CortexForceGraph3DCanvas'));

const DIM_STORAGE_KEY = 'vibe_designer_cortex_graph_dim';

function readInitialDim(): '2d' | '3d' {
  try {
    return localStorage.getItem(DIM_STORAGE_KEY) === '2d' ? '2d' : '3d';
  } catch {
    return '3d';
  }
}

export function CortexGraphView() {
  const graph = useKnowledgeStore((s) => s.cortexGraph);
  const isFetching = useKnowledgeStore((s) => s.isFetchingCortexGraph);
  const error = useKnowledgeStore((s) => s.cortexGraphError);
  const fetchCortexGraph = useKnowledgeStore((s) => s.fetchCortexGraph);
  // 1.191.0 Phase 7.6.1 — re-fetch on SSE ingest event.
  const lastIngestAt = useKnowledgeStore((s) => s.lastIngestAt);
  // WS-A1 — 2D/3D toggle (localStorage-persisted, defaults 3D).
  const [dimension, setDimension] = useState<'2d' | '3d'>(readInitialDim);

  useEffect(() => {
    void fetchCortexGraph();
  }, [fetchCortexGraph, lastIngestAt]);

  useEffect(() => {
    try {
      localStorage.setItem(DIM_STORAGE_KEY, dimension);
    } catch {
      // best-effort persistence; in-memory state stays correct
    }
  }, [dimension]);

  return (
    <section className="rounded border border-vibe-border bg-vibe-bg-secondary p-3 mb-4">
      <header className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary flex items-center gap-1.5">
          <Share2 className="w-3 h-3" />
          Entity graph
          {graph && graph.nodes.length > 0 && (
            <span className="ml-2 text-vibe-text-secondary tabular-nums">
              {graph.nodes.length} node{graph.nodes.length === 1 ? '' : 's'} · {graph.edges.length} edge{graph.edges.length === 1 ? '' : 's'}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <div
            role="group"
            aria-label="Graph dimension"
            className="flex items-center gap-0.5 rounded bg-vibe-bg-tertiary p-0.5"
          >
            {(['2d', '3d'] as const).map((dim) => (
              <button
                key={dim}
                data-testid={`cortex-graph-toggle-${dim}`}
                type="button"
                onClick={() => setDimension(dim)}
                aria-pressed={dimension === dim}
                className={
                  dimension === dim
                    ? 'px-2 py-0.5 text-[10px] font-semibold rounded bg-vibe-accent text-vibe-bg'
                    : 'px-2 py-0.5 text-[10px] font-semibold rounded text-vibe-text-secondary hover:text-vibe-text'
                }
              >
                {dim.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            data-testid="cortex-graph-refresh"
            type="button"
            onClick={() => void fetchCortexGraph()}
            disabled={isFetching}
            className="px-2 py-1 text-xs rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary disabled:opacity-50 flex items-center gap-1"
          >
            {isFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            <span>Refresh</span>
          </button>
        </div>
      </header>

      {error && (
        <div
          data-testid="cortex-graph-network-error"
          className="rounded border border-vibe-error/30 bg-vibe-error/10 p-2 text-xs text-vibe-error"
        >
          {error.message}
        </div>
      )}

      {!error && graph && graph.error && (
        <div
          data-testid="cortex-graph-error"
          className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-2 text-xs text-vibe-warning"
        >
          {graph.error}
        </div>
      )}

      {!error && graph && !graph.error && graph.nodes.length === 0 && (
        <div
          data-testid="cortex-graph-empty"
          className="text-xs text-vibe-text-secondary py-2"
        >
          No entities indexed yet — CORTEX has no nodes for this tenant.
        </div>
      )}

      {!error && graph && !graph.error && graph.nodes.length > 0 && (
        <div
          data-testid="cortex-graph-canvas"
          className="h-96 w-full bg-vibe-bg rounded overflow-hidden border border-vibe-border"
        >
          <Suspense fallback={<div className="text-xs text-vibe-text-secondary p-2">Loading graph…</div>}>
            {dimension === '3d' ? (
              <ForceGraphCanvas3D nodes={graph.nodes} edges={graph.edges} />
            ) : (
              <ForceGraphCanvas2D nodes={graph.nodes} edges={graph.edges} />
            )}
          </Suspense>
        </div>
      )}
    </section>
  );
}
